// CB:BEGIN admin-command-center
/**
 * AdminCommandCenter — Main admin dashboard content.
 *
 * Layout (top to bottom):
 *   1. KPI metric strip (AdminStatsCards)
 *   2. Stalled project alerts (AdminAlertsPanel) — only visible if alerts exist
 *   3. Project health bar (AdminHealthCards)
 *   4. Quick access actions
 *   5. Platform Users section
 */
import React from 'react';
import AdminStatsCards from './AdminStatsCards';
import AdminAlertsPanel from './AdminAlertsPanel';
import AdminHealthCards from './AdminHealthCards';

interface Action {
  id: string;
  label: string;
  description: string;
  screen: string;
  navigatePath?: string;
  gradient: string;
  icon: React.ReactNode;
  priority?: 'primary' | 'secondary';
}

// ─── SVG icons (white stroke, used inside colored icon cells) ─────────────────

const IconHouse = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);
const IconFolder = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
  </svg>
);
const IconShield = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);
const IconDollar = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);
const IconSearch = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const IconChart = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);
const IconDocuments = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);
const IconStar = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);
const IconBell = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);
const IconUsers = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// ─── Quick actions ────────────────────────────────────────────────────────────

const QUICK_ACTIONS: Action[] = [
  {
    id: 'projects',
    label: 'Projects',
    description: 'View and manage all active and completed projects',
    screen: 'admin-projects-list',
    gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
    icon: <IconFolder />,
    priority: 'primary',
  },
  {
    id: 'verification',
    label: 'Verification Queue',
    description: 'Review pending contractor verification submissions',
    screen: 'admin-verification-queue',
    gradient: 'linear-gradient(135deg, #d97706, #b45309)',
    icon: <IconShield />,
    priority: 'primary',
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'Platform revenue, escrow balances, invoices',
    screen: 'admin-finance',
    gradient: 'linear-gradient(135deg, #0284c7, #0369a1)',
    icon: <IconDollar />,
  },
  {
    id: 'operations',
    label: 'Operations',
    description: 'Search projects by ID, address, email, or escrow ID',
    screen: 'admin-operations',
    gradient: 'linear-gradient(135deg, #374151, #1f2937)',
    icon: <IconSearch />,
  },
  {
    id: 'alerts',
    label: 'Alerts',
    description: 'Review stalled projects and platform alerts',
    screen: 'admin-alerts',
    gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)',
    icon: <IconBell />,
  },
  // CB:BEGIN admin-dispute-center-action
  {
    id: 'disputes',
    label: 'Dispute Center',
    description: 'Resolve open disputes between homeowners and contractors',
    screen: 'admin-disputes',
    gradient: 'linear-gradient(135deg, #be123c, #9f1239)',
    icon: <IconShield />,
  },
  // CB:END admin-dispute-center-action
  // CB:BEGIN admin-marketplace-health-action
  {
    id: 'marketplace',
    label: 'Marketplace Health',
    description: 'Supply/demand metrics, bid velocity, contractor coverage',
    screen: 'admin-marketplace-health',
    gradient: 'linear-gradient(135deg, #0891b2, #0e7490)',
    icon: <IconChart />,
  },
  // CB:END admin-marketplace-health-action
  {
    id: 'reviews',
    label: 'Reviews',
    description: 'Moderate homeowner reviews of contractors',
    screen: 'admin-reviews',
    gradient: 'linear-gradient(135deg, #db2777, #be185d)',
    icon: <IconStar />,
  },
  {
    id: 'support-requests',
    label: 'Support Requests',
    description: 'Manage user support requests and tickets',
    screen: 'admin-support-requests',
    gradient: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
    icon: <IconBell />,
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'View and manage platform documents',
    screen: 'admin-documents',
    gradient: 'linear-gradient(135deg, #0891b2, #0e7490)',
    icon: <IconDocuments />,
  },
];

const PLATFORM_USERS = [
  { id: 'homeowners',  label: 'Homeowners',        screen: 'admin-homeowners-list',  color: '#2563eb', bg: '#eff6ff' },
  { id: 'contractors', label: 'Contractors',        screen: 'admin-contractors-list', color: '#059669', bg: '#f0fdf4' },
  { id: 'realtors',    label: 'Realtors',           screen: 'admin-realtors',         color: '#7c3aed', bg: '#f5f3ff' },
  { id: 'pms',         label: 'Property Managers',  screen: 'admin-property-managers',color: '#123c3b', bg: '#f0faf9' },
];

// ─── Section header ────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 12 }}>
    {children}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

interface AdminCommandCenterProps {
  onNavigate?: (screen: string) => void;
}

const AdminCommandCenter: React.FC<AdminCommandCenterProps> = ({ onNavigate }) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Command Center</h1>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0 0' }}>CoreBids platform overview</p>
        </div>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{dateStr}</span>
      </div>

      {/* ── KPI strip ── */}
      <section style={{ marginBottom: 20 }}>
        <AdminStatsCards onNavigate={onNavigate} />
      </section>

      {/* ── Stalled alerts (shown only when > 0) ── */}
      <AdminAlertsPanel onNavigate={onNavigate} />

      {/* ── Project health ── */}
      <section style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 28,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <AdminHealthCards onNavigate={onNavigate} />
      </section>

      {/* ── Quick access ── */}
      <section style={{ marginBottom: 28 }}>
        <SectionLabel>Quick Access</SectionLabel>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 10,
        }}>
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => onNavigate?.(action.navigatePath ?? action.screen)}
              data-testid={`qa-${action.id}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '14px 16px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'box-shadow 0.12s, border-color 0.12s',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                el.style.borderColor = '#c7d2fe';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.boxShadow = 'none';
                el.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={{
                width: 38, height: 38, flexShrink: 0,
                background: action.gradient, borderRadius: 9,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff',
              }}>
                {action.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
                  {action.label}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                  {action.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Platform users ── */}
      {/* CB:BEGIN platform-users-section */}
      <section id="platform-users" style={{ marginBottom: 8 }}>
        <SectionLabel>Platform Users</SectionLabel>
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '18px 20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 36, height: 36, flexShrink: 0,
              background: 'linear-gradient(135deg, #123c3b, #0d2b2a)',
              borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
            }}>
              <IconUsers />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>All User Accounts</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Manage homeowners, contractors, realtors, and property managers</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {PLATFORM_USERS.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate?.(item.screen)}
                data-testid={`pu-${item.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: item.bg,
                  border: `1px solid ${item.color}30`,
                  borderRadius: 8,
                  padding: '10px 14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color 0.12s',
                  color: item.color,
                  fontSize: 13,
                  fontWeight: 600,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = item.color; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = `${item.color}30`; }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 3, background: item.color, flexShrink: 0, display: 'inline-block' }} />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* CB:END platform-users-section */}

    </div>
  );
};

export default AdminCommandCenter;
// CB:END admin-command-center
